// Promos de venta en los Extras de «Arma la venta»: banner de categoría, precio por unidad con descuento y burbujas propias.
// La de categoría va solo en el banner y en el precio tachado; la tarjeta no la repite.
import { Gift, Smartphone, Tag } from 'lucide-react';
import type { PromoBannerRow } from '@/domains/sales/wizard/components/PromoBanner';
import type { BenefitBubble, CardSaleBenefits } from '@/domains/sales/wizard/promotionSlots';
import type { CatalogItem, ReservedLine } from '@/domains/sales/wizard/types';
import type { StoreSalePromotionItem } from '../api/wizardPromotionsContract';
import {
    bannerRowOf,
    discountLabel,
    matchesList,
    otherLevers,
    reachDetail,
    withTone,
    type LeverText,
    type ProductCategory,
} from './saleBenefits';

/** Categorías que viven en Extras; el banner aclara las que la promo deja fuera. */
const EXTRAS_CATEGORIES: readonly ProductCategory[] = ['ACCESSORY', 'MODEM', 'SIM'];

const BASIS_POINTS = 10_000;

function isItemSpecific(promo: StoreSalePromotionItem): boolean {
    return promo.scope.skus !== null || promo.scope.tiers !== null;
}

/** Categorías de Extras que hay en el catálogo. */
function presentCategories(items: readonly CatalogItem[]): Set<ProductCategory> {
    return new Set(items.flatMap((i) => (i.category ? [i.category] : [])));
}

/** Va al banner de Extras: sin SKUs ni gamas, y baja accesorios o se dispara con un extra que hay. */
export function isExtrasWide(
    promo: StoreSalePromotionItem,
    present: ReadonlySet<ProductCategory>,
): boolean {
    if (isItemSpecific(promo)) return false;
    if (promo.levers.accessoryDiscount) return true;
    const categories = promo.scope.productCategories;
    return categories !== null && categories.some((c) => present.has(c));
}

/** Lo que el precio de un extra necesita saber de la venta. */
export interface ExtrasSaleContext {
    /** Promo de venta elegida; `null` con «Ninguna». */
    chosen: StoreSalePromotionItem | null;
    /** Líneas de extras que ya tiene la venta, con el descuento que les puso Promociones. */
    extraLines: readonly ReservedLine[];
    /** Equipo de la venta; con él se sabe qué regalos le tocan. */
    anchorLine?: ReservedLine;
}

export const NO_EXTRAS_SALE: ExtrasSaleContext = { chosen: null, extraLines: [] };

/** La promo alcanza al equipo de la venta por SKU, gama y categoría. */
export function reachesAnchor(
    promo: Pick<StoreSalePromotionItem, 'scope'>,
    anchor: ReservedLine,
): boolean {
    const { skus, tiers, productCategories } = promo.scope;
    return (
        matchesList(skus, anchor.sku) &&
        matchesList(tiers, anchor.product?.tier ?? undefined) &&
        matchesList(productCategories, anchor.product?.category)
    );
}

/** El extra es una opción de regalo de una promo que le toca al equipo de la venta. */
export function isGiftOption(
    item: CatalogItem,
    promotions: readonly StoreSalePromotionItem[],
    anchor: ReservedLine | undefined,
): boolean {
    if (!anchor) return false;
    return promotions.some(
        (p) =>
            p.levers.gift?.mode === 'LIST' &&
            (p.levers.gift.skus ?? []).includes(item.sku) &&
            reachesAnchor(p, anchor),
    );
}

/** Neto por unidad de las piezas de ese SKU que ya están en la venta; `null` si no tienen descuento. */
function soldUnitPrice(sku: string, lines: readonly ReservedLine[]): number | null {
    const own = lines.filter((l) => l.sku === sku);
    const qty = own.reduce((n, l) => n + l.qty, 0);
    const discount = own.reduce((n, l) => n + (l.discountCents ?? 0), 0);
    if (qty === 0 || discount <= 0) return null;
    const gross = own.reduce((n, l) => n + (l.basePriceCents ?? 0) * l.qty, 0);
    return Math.round((gross - discount) / qty);
}

/** Descuento de una pieza más con la promo elegida: el $ fijo y el tope son por venta, así que resta lo ya usado. */
function nextUnitDiscount(
    priceCents: number,
    promo: StoreSalePromotionItem,
    lines: readonly ReservedLine[],
): number {
    const lever = promo.levers.accessoryDiscount;
    if (!lever) return 0;
    const used = lines
        .filter((l) => l.promotionId === promo.promotionId && l.product?.category === 'ACCESSORY')
        .reduce((n, l) => n + (l.discountCents ?? 0), 0);
    const budget =
        lever.type === 'AMOUNT'
            ? Math.min(lever.amountCents ?? 0, lever.maxAmountCents ?? Number.MAX_SAFE_INTEGER)
            : (lever.maxAmountCents ?? Number.MAX_SAFE_INTEGER);
    const wanted =
        lever.type === 'AMOUNT'
            ? priceCents
            : Math.floor((priceCents * (lever.percentBps ?? 0)) / BASIS_POINTS);
    return Math.max(0, Math.min(priceCents, wanted, budget - used));
}

/** Precio por unidad: el que ya tiene en la venta o el que le daría la promo ELEGIDA; nada con «Ninguna». */
export function extraDiscountedPriceFor(item: CatalogItem, sale: ExtrasSaleContext): number | null {
    const sold = soldUnitPrice(item.sku, sale.extraLines);
    if (sold !== null) return sold;
    if (item.category !== 'ACCESSORY' || sale.chosen === null) return null;
    const discount = nextUnitDiscount(item.cashPriceCents, sale.chosen, sale.extraLines);
    return discount > 0 ? item.cashPriceCents - discount : null;
}

/** El descuento del equipo dicho desde Extras: «−10 % en el equipo». */
function deviceLever(promo: StoreSalePromotionItem): LeverText | null {
    const device = promo.levers.deviceDiscount;
    if (!device) return null;
    const text = `${discountLabel(device)} en el equipo`;
    return { key: 'device', icon: Smartphone, label: text, phrase: text };
}

/** La promo apunta a este extra: lo nombra por SKU o por su categoría (y gama, si la pide). */
export function targetsExtra(
    item: Pick<CatalogItem, 'sku' | 'category' | 'tier'>,
    promo: Pick<StoreSalePromotionItem, 'scope'>,
): boolean {
    const { skus, tiers, productCategories } = promo.scope;
    if (skus !== null) return skus.includes(item.sku);
    if (productCategories === null || item.category === undefined) return false;
    return productCategories.includes(item.category) && matchesList(tiers, item.tier);
}

/** Burbujas de las promos que apuntan al extra, con el color de su grupo; las de toda la tienda no. */
export function extraBubblesFor(
    item: CatalogItem,
    promotions: readonly StoreSalePromotionItem[],
): BenefitBubble[] {
    return promotions
        .filter((p) => targetsExtra(item, p))
        .sort((a, b) => a.name.localeCompare(b.name, 'es-MX'))
        .flatMap((promo) => {
            const device = isItemSpecific(promo) ? deviceLever(promo) : null;
            const levers = [...(device ? [device] : []), ...otherLevers(promo.levers)].filter(
                (lever) => lever.key !== 'accessory' && lever.key !== 'gift',
            );
            return levers.map((lever) => ({
                key: `extra-${lever.key}-${promo.promotionId}`,
                label: lever.label,
                icon: lever.icon,
                title: promo.name,
                ...withTone(lever.key),
            }));
        });
}

export function extraSaleBenefits(
    item: CatalogItem,
    promotions: readonly StoreSalePromotionItem[],
    sale: ExtrasSaleContext = NO_EXTRAS_SALE,
): CardSaleBenefits {
    const discounted = extraDiscountedPriceFor(item, sale);
    const accessory = sale.chosen?.levers.accessoryDiscount;
    const discountBubble: BenefitBubble[] =
        discounted !== null && sale.chosen && accessory
            ? [
                  {
                      key: `extra-discount-${sale.chosen.promotionId}`,
                      label: discountLabel(accessory),
                      icon: Tag,
                      title: sale.chosen.name,
                  },
              ]
            : [];
    const giftBubble: BenefitBubble[] = isGiftOption(item, promotions, sale.anchorLine)
        ? [{ key: 'extra-gift', label: 'Puede ser tu regalo', icon: Gift, tone: 'gift' }]
        : [];
    return {
        bubbles: [...giftBubble, ...discountBubble, ...extraBubblesFor(item, promotions)],
        discountedPriceCents: discounted,
    };
}

/** Filas del banner de Extras, en orden alfabético; primero lo que baja los accesorios. */
export function extrasBannerRows(
    promotions: readonly StoreSalePromotionItem[],
    items: readonly CatalogItem[],
): PromoBannerRow[] {
    const present = presentCategories(items);
    const exclusions = EXTRAS_CATEGORIES.filter((c) => present.has(c));
    return promotions
        .filter((p) => isExtrasWide(p, present))
        .sort((a, b) => a.name.localeCompare(b.name, 'es-MX'))
        .map((promo) => {
            const levers = otherLevers(promo.levers);
            const device = deviceLever(promo);
            if (device) levers.push(device);
            const accessory = levers.findIndex((l) => l.key === 'accessory');
            const [main] = levers.splice(accessory >= 0 ? accessory : 0, 1);
            // El descuento en accesorios los alcanza aunque la promo se dispare con otra categoría.
            const left = promo.levers.accessoryDiscount
                ? exclusions.filter((c) => c !== 'ACCESSORY')
                : exclusions;
            return bannerRowOf(promo, main, levers, reachDetail(promo, items, left));
        });
}

/** Si alguna fila del banner baja el precio de los accesorios. */
export function extrasBannerLowersPrice(promotions: readonly StoreSalePromotionItem[]): boolean {
    return promotions.some((p) => !isItemSpecific(p) && p.levers.accessoryDiscount !== null);
}
